import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';

import Loader from '@/components/Loader';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import Drawer, { DrawerBody, DrawerFooter, DrawerHeader } from '@/design_system/Drawer';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import { useShowToast } from '@/design_system/Toast';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconCancel from '@/icons/Cancel.svg';
import {
  InvoiceFromGet,
  useDeleteInvoice,
  useInvoice,
  useReportIssue,
  useSubmitInvoice,
  useValidateInvoice,
} from '@/models/invoice';
import { getAmountWithQuantity } from '@/models/invoiceLine';
import { hasPermissionOnAnyScope } from '@/models/user';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';
import { formatCurrency } from '@/utils/number';
import useViewPort from '@/utils/useViewport';

import { InvoiceAmount } from './components/InvoiceAmount';
import { InvoiceDestination } from './components/InvoiceDestination';
import { InvoiceIssue } from './components/InvoiceIssue';
import { InvoiceIssuingDate } from './components/InvoiceIssuingDate';
import { InvoiceLines } from './components/InvoiceLines';
import { InvoiceMedia } from './components/InvoiceMedia';
import { InvoiceNumber } from './components/InvoiceNumber';
import { InvoiceOrigin } from './components/InvoiceOrigin';
import { InvoicePaymentDueDate } from './components/InvoicePaymentDueDate';
import { InvoicePaymentReceipt } from './components/InvoicePaymentReceipt';
import { InvoicePaymentStrategy } from './components/InvoicePaymentStrategy';

export const Invoice = () => {
  const { t } = useLingui();
  const { id } = useParams();
  const showToast = useShowToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSession } = useCurrentSession();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(true);

  const { data: invoice, error } = useInvoice(id);
  const { mutate: deleteInvoice } = useDeleteInvoice(id!);

  const isCreatingInvoice =
    !!invoice && currentSession?.id === invoice.creatorId && invoice.status === 'draft';

  const close = () => {
    setIsOpen(false);
    queryClient.invalidateQueries({ queryKey: ['invoices'] });

    // Wait for the Drawer CSS exit animation to finish before navigating away
    setTimeout(() => {
      navigate((location.state?.onCancel as string) ?? '/accounting');
    }, 150);
  };

  const deleteAndClose = () => {
    setIsOpen(false);

    // Wait for the Drawer CSS exit animation to finish before navigating away
    setTimeout(() => {
      deleteInvoice();

      navigate((location.state?.onCancel as string) ?? '/accounting', { replace: true });
    }, 150);
  };

  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        text: t({
          id: 'invoice.fetch.error',
          message: 'Unable to retrieve the invoice. Please try again later.',
        }),
      });

      close();
    }
    // Prevents the toast from being displayed twice
  }, [error]);

  return (
    <Drawer
      isOpen={isOpen}
      position="bottom"
      onOpenChange={isCreatingInvoice ? deleteAndClose : close}
      closeConfirmation={
        isCreatingInvoice
          ? {
              title: <Trans id="invoice.close.title">Discard invoice</Trans>,
              content: (
                <Trans id="invoice.close.content">
                  You're about to leave this page without saving your changes. The invoice draft
                  will be discarded.
                  <br />
                  Are you sure?
                </Trans>
              ),
              confirm: <Trans id="invoice.close.confirm">Discard</Trans>,
            }
          : undefined
      }
    >
      {!invoice ? <InvoiceLoading /> : <InvoiceContent invoice={invoice} onClose={close} />}
    </Drawer>
  );
};

const InvoiceLoading = () => {
  return (
    <>
      <DrawerHeader>
        <Title size="200" as="h1">
          <Trans id="invoice.loading.title">Invoice</Trans>
        </Title>
      </DrawerHeader>
      <DrawerBody>
        <Stack alignItems="center" justifyContent="center" style={{ height: '100%' }}>
          <Loader />
        </Stack>
      </DrawerBody>
      <DrawerFooter />
    </>
  );
};

interface InvoiceContextData {
  invoice: InvoiceFromGet;
  showErrors: boolean;
  errors: {
    number: boolean;
    origin: boolean;
    destination: boolean;
    issuingDate: boolean;
    paymentDueDate: boolean;
    amount: boolean;
    lines: boolean;
    media: boolean;
  };
  isDisabled: boolean;
  isCreatingInvoice: boolean;
  isValidatingInvoice: boolean;
}

const InvoiceContext = createContext({} as InvoiceContextData);

export const useInvoiceContext = () => useContext(InvoiceContext);

const InvoiceContent = ({ invoice, onClose }: { invoice: InvoiceFromGet; onClose: () => void }) => {
  const { isMobile } = useViewPort();
  const { currentSession } = useCurrentSession();
  const [currentOrganization] = useCurrentOrganization();

  const [showErrors, setShowErrors] = useState(false);
  const isCreatingInvoice = currentSession?.id === invoice.creatorId && invoice.status === 'draft';
  const isValidatingInvoice =
    !!invoice.destinationCompanyId &&
    hasPermissionOnAnyScope(currentSession, 'validate_invoice', currentOrganization?.id) &&
    invoice.status === 'pending-validation';
  const isDisabled = !isCreatingInvoice && !isValidatingInvoice;

  const errors = {
    number: !invoice.number,
    origin: !invoice.originCompany,
    destination: !invoice.destinationCompany,
    issuingDate: !invoice.issuingDate,
    paymentDueDate: !invoice.paymentDueDate,
    amount: !invoice.amount,
    lines: !invoice.lines.length,
    media: !invoice.media.filter((medium) => medium.type === 'invoice').length,
  };

  return (
    <InvoiceContext
      value={{
        invoice,
        showErrors,
        errors,
        isDisabled,
        isCreatingInvoice,
        isValidatingInvoice,
      }}
    >
      <DrawerHeader>
        <Title size="200" as="h1">
          {invoice.status === 'draft' ? (
            <Trans id="invoice.title.new">New invoice</Trans>
          ) : (
            <Trans id="invoice.title">Invoice {invoice.number}</Trans>
          )}
        </Title>
      </DrawerHeader>
      <DrawerBody className="text-primary">
        <Stack gap="16px" padding={isMobile ? '16px' : '24px 32px'} style={{ minHeight: '100%' }}>
          <InvoiceIssue />
          <Stack row gap="32px" style={{ flex: '1' }}>
            <InvoiceMedia />
            <Stack gap="1rem" style={{ flex: '1 1 650px', maxWidth: '100%' }}>
              <InvoiceNumber />
              <Stack row gap="1rem">
                <InvoiceOrigin />
                <InvoiceIssuingDate />
              </Stack>
              <Stack row gap="1rem">
                <InvoiceDestination />
                <InvoicePaymentDueDate />
              </Stack>
              <InvoicePaymentStrategy />
              <InvoiceAmount />
              <InvoiceLines />
              <InvoicePaymentReceipt />
            </Stack>
          </Stack>
        </Stack>
      </DrawerBody>
      <DrawerFooter>
        <InvoiceFooter onClose={onClose} setShowErrors={setShowErrors} />
      </DrawerFooter>
    </InvoiceContext>
  );
};

const InvoiceFooter = ({
  onClose,
  setShowErrors,
}: {
  onClose: () => void;
  setShowErrors: (showErrors: boolean) => void;
}) => {
  const { invoice, showErrors, errors, isDisabled, isCreatingInvoice, isValidatingInvoice } =
    useInvoiceContext();

  const [apiError, setApiError] = useState<string | null>(null);

  const { mutateAsync: submitInvoice, isPending: isSubmitInvoicePending } = useSubmitInvoice(
    invoice.id
  );
  const { mutateAsync: validateInvoice, isPending: isValidateInvoicePending } = useValidateInvoice(
    invoice.id
  );

  const hasErrors = Object.values(errors).some((value) => value);

  const onAction = (action: () => Promise<void>) => async () => {
    setShowErrors(true);

    if (hasErrors) {
      return;
    }

    setApiError(null);

    try {
      await action();
      onClose();
    } catch (err: any) {
      setApiError((err.message as string) ?? '');
    }
  };

  return (
    <Stack row gap="24px" alignItems="center" justifyContent="flex-end">
      {showErrors && hasErrors && (
        <Message type="error">
          <Trans id="invoice.form.global-error">Please fill all the required fields</Trans>
        </Message>
      )}
      {apiError !== null && (
        <InlineText color="danger">
          {apiError ?? (
            <Trans id="invoice.form.api-error.unknown">
              An unknown error occurred. Please try again later.
            </Trans>
          )}
        </InlineText>
      )}
      <Stack alignItems="flex-end">
        <InlineText size="200" color="secondary">
          <Trans id="invoice.form.lines-total-amount">Services total amount</Trans>
        </InlineText>
        <InlineText size="50" weight="medium">
          {invoice.lines.length
            ? formatCurrency(
                invoice.lines.reduce((acc, line) => acc + getAmountWithQuantity(line.price), 0),
                invoice.currency
              )
            : '-'}
        </InlineText>
      </Stack>
      <div style={{ height: '24px', borderLeft: '1px solid var(--color-neutral-300)' }} />
      {isDisabled && (
        <Button variant="secondary" onPress={onClose}>
          <Trans id="invoice.form.close">Close</Trans>
        </Button>
      )}
      {isCreatingInvoice && (
        <Button
          variant="primary"
          onPress={onAction(submitInvoice)}
          isLoading={isSubmitInvoicePending}
        >
          <Trans id="invoice.form.submit">Submit</Trans>
        </Button>
      )}
      {isValidatingInvoice && (
        <Stack row gap="16px" alignItems="center">
          <ReportIssue isDisabled={isValidateInvoicePending} />
          <Button
            variant="primary"
            onPress={onAction(validateInvoice)}
            isLoading={isValidateInvoicePending}
          >
            <Trans id="invoice.form.validate">Validate</Trans>
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

const ReportIssue = ({ isDisabled }: { isDisabled: boolean }) => {
  const { t } = useLingui();
  const { invoice } = useInvoiceContext();

  const [isOpen, setIsOpen] = useState(false);
  const [issue, setIssue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const { mutateAsync: reportIssue, isPending: isReportIssuePending } = useReportIssue(invoice.id);

  const onReportIssue = async () => {
    setShowErrors(true);
    setError(null);

    if (!issue) {
      return;
    }

    try {
      await reportIssue({ issue });
      setIsOpen(false);
    } catch (err: any) {
      setError((err.message as string) ?? '');
    }
  };

  return (
    <>
      <Button variant="secondary" disabled={isDisabled} onPress={() => setIsOpen(true)}>
        <IconCancel />
        <Trans id="invoice.form.report-issue">Report an issue</Trans>
      </Button>
      <Dialog
        title={<Trans id="invoice.form.report-issue.title">Report an issue</Trans>}
        isOpen={isOpen}
        onOpenChange={(isOpen) => {
          if (!isReportIssuePending) {
            setIsOpen(isOpen);
          }
        }}
        style={{ maxWidth: '550px' }}
      >
        <main>
          <Stack gap="8px">
            <Paragraph>
              <Trans id="invoice.form.report-issue.text">
                If the invoice seems invalid, you can report it. This will archive it and notify the
                store and accounting team to resolve the issue.
              </Trans>
            </Paragraph>
            <TextArea
              rows={3}
              value={issue}
              onChange={(event) => setIssue(event.target.value)}
              placeholder={t({
                id: 'invoice.form.report-issue.placeholder',
                message: 'Explain the issue...',
              })}
              error={
                showErrors && !issue
                  ? t({
                      id: 'invoice.form.report-issue.no-issue',
                      message: 'Please explain the issue',
                    })
                  : undefined
              }
            />
          </Stack>
        </main>
        <Footer>
          {error !== null && (
            <FooterError>
              <Trans id="invoice.form.report-issue.error">
                An unknown error occurred. Please try again later.
              </Trans>
            </FooterError>
          )}
          <FooterButtons>
            <Button variant="danger" onPress={onReportIssue} isLoading={isReportIssuePending}>
              <Trans id="invoice.form.report-issue.report">Report an issue</Trans>
            </Button>
          </FooterButtons>
        </Footer>
      </Dialog>
    </>
  );
};
